/**
 * THE INPUTS, one per kind of answer region — the part of the student page that is specific to this
 * product rather than generic form rendering.
 *
 * THE REGION DECIDES, NOT THE TASK TYPE. Every answerable task declares an answer region with a type (see
 * `answer-region` in typst/exam.typ), and that type is what was PRINTED: ruled lines, squared paper, an
 * empty box, numbered lines, or nothing because the answer sits in the task's own body. Picking the input
 * from the region rather than re-deriving it from `TaskType` means the online field can never contradict
 * the paper version of the same exam — a task printed on Karopapier gets a canvas, full stop.
 *
 * WHAT EACH REGION BECOMES (`answerKindForRegion`):
 *   lines           → a text field, sitting on the printed lines.
 *   numbered-lines  → one field per printed line, each on its own number.
 *   grid / blank    → a freehand canvas, because this is where notation and drawings go.
 *   inline          → the structured widget the body holds — a checklist, two columns to match, a
 *                     coordinate system, a text scattered with blanks (GAP_TEXT's `#gap`), or a numbered
 *                     "source -> ______" list (TRANSLATION's `#prompt-list`). This is the ONE case that
 *                     still consults the task type, because only it says which of those the body holds.
 *
 * WHERE THE STRUCTURE FOR `inline` COMES FROM, and its weak spot. Multiple choice is clean: the template
 * emits its options as real metadata and `assembleTaskNodes` appends them to the description as a
 * canonical Markdown checklist, so parsing them back is reading a format we wrote ourselves. Matching is
 * parsed out of the printed two-column text (`match-columns` text-markers it as "1. left" / "A) right") —
 * it works, but it is parsing prose, and the durable fix is a structured `match` metadata block next to
 * the `mc` one.
 */

import {
  memo,
  useCallback,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { X } from 'lucide-react';
import type { AnswerType } from '../../../db/api/exam-creation/exam-structure.js';
import { TaskType } from '../../../db/api/task-types/task-main.js';
import { DrawingCanvas, type PageCrop, type SavedStroke } from './drawing-canvas.js';

export type AnswerKind = 'none' | 'text' | 'drawing' | 'choice' | 'mapping' | 'labels' | 'gapFill' | 'promptList';

/** Everything a student can have entered for one task. Exactly one of these is set, per the task's kind. */
export interface TaskAnswerState {
  text?: string;
  drawing?: string | null;
  /**
   * The same drawing as the strokes it was made of, saved alongside the rendered PNG above. The PNG is what
   * gets submitted and printed; this is what lets the editor pick the work back up — reopening a draft
   * restores real strokes that erase and undo like anything drawn this session, instead of a flat bitmap
   * that only "Clear" could touch. See `SavedStroke`.
   */
  strokes?: SavedStroke[];
  /** Indices into the parsed option list. */
  choices?: number[];
  /**
   * Left-hand row index → every right-hand letter it's been joined to. An array, not a single letter,
   * because matching allows many-to-many: one left item can join several right ones and vice versa (see
   * `OverlayMapping`) — the way a paper answer can legitimately draw two lines out of the same row.
   */
  mapping?: Record<number, string[]>;
  labels?: string[];
  /**
   * `gapFill`/`promptList` — one entry per printed blank (a `#gap` in the task's flowing text, or a
   * `#prompt-list` row's own field), in order. A separate field from `labels` despite the identical shape:
   * `labels` is specifically LABEL_IMAGE's callout/numbered-line naming convention (see `withLabel`), and
   * conflating the two would make a reader guess which widget actually wrote a given task's state.
   */
  blanks?: string[];
}

/**
 * Types that are answered by drawing rather than typing. The notation-heavy ones (calculation, proof,
 * chemistry) are deliberately in here and not in the text family: a keyboard cannot place a fraction bar
 * or a bond angle where the answer needs it, and they are the reason drawing exists on this page at all.
 */
const DRAWING_TYPES = new Set<string>([
  TaskType.SKETCH,
  TaskType.DIAGRAM,
  TaskType.ART,
  TaskType.GEOMETRY,
  TaskType.PLOTTING,
  TaskType.CURVE_ANALYSIS,
  TaskType.CALCULATION,
  TaskType.MATHEMATICAL_PROOF,
  TaskType.CHEMICAL_FORMULA,
  TaskType.MUSIC,
]);

/**
 * Which input a task gets, decided by the ANSWER REGION the exam declared — not by guessing from the task
 * type.
 *
 * The region is the authority because it is also what was printed: a task whose sheet shows squared paper
 * gets a canvas, one whose sheet shows ruled lines gets a text field, and the two can never disagree with
 * what the student would have had on paper. The task type is consulted for exactly one case — `inline`,
 * where the answer sits in the task's own body and only the type says whether that body holds a checklist,
 * two columns to match, or a coordinate system to draw in.
 */
export function answerKindForRegion(answerType: AnswerType | undefined, taskType: string | undefined): AnswerKind {
  switch (answerType) {
    case 'lines':
      return 'text';
    case 'numbered-lines':
      return 'labels';
    case 'grid':
    case 'blank':
      return 'drawing';
    case 'inline':
      if (taskType === TaskType.MULTIPLE_CHOICE) return 'choice';
      if (taskType === TaskType.MAPPING) return 'mapping';
      if (taskType === TaskType.GAP_TEXT) return 'gapFill';
      if (taskType === TaskType.TRANSLATION) return 'promptList';
      // A #grid-paper coordinate system in the body is drawn into, not typed into.
      if (taskType && DRAWING_TYPES.has(taskType)) return 'drawing';
      return 'text';
    default:
      return 'none';
  }
}

// ─── parsing the structure back out of a description ────────────────────────────────────────────────

// eslint-disable-next-line require-unicode-regexp -- matches the repo's es2020 target, see exam-structure.ts
const CHECKLIST_LINE = /^\s*[-*]\s*\[[ xX]?\]\s+(?<label>.+?)\s*$/u;
// eslint-disable-next-line require-unicode-regexp
const NUMBERED_LINE = /^\s*(?<index>\d+)[.)]\s+(?<label>.+?)\s*$/u;
// eslint-disable-next-line require-unicode-regexp
const LETTERED_LINE = /^\s*(?<letter>[A-Z])\)\s+(?<label>.+?)\s*$/u;

/**
 * ONLY THE STRUCTURE IS READ BACK, never the prose around it. Both parsers used to also return the
 * description with the structured lines stripped out, for the first version of this page — the one that
 * reflowed the task tree and typeset the prompt itself. This page doesn't: the prompt a student reads is
 * the printed page under the widget, so there is nothing left for that text to be used for.
 */
export interface ParsedChoice {
  options: string[];
}

export function parseChoice(description: string): ParsedChoice {
  const options: string[] = [];
  for (const line of (description ?? '').split('\n')) {
    const match = CHECKLIST_LINE.exec(line);
    if (match?.groups?.label) options.push(match.groups.label);
  }
  return { options };
}

export interface ParsedMapping {
  left: string[];
  right: string[];
}

/**
 * Reads the two printed columns back. Only ever called for a MAPPING task, which is why a plain numbered
 * list is safe to treat as the left column here — see the file header on replacing this with real metadata.
 *
 * Fewer than two on either side is not a matching task's columns but a numbered or lettered list that
 * happens to look like one, so it yields nothing rather than half a pairing.
 */
export function parseMapping(description: string): ParsedMapping {
  const left: string[] = [];
  const right: string[] = [];

  for (const line of (description ?? '').split('\n')) {
    const numbered = NUMBERED_LINE.exec(line);
    const lettered = LETTERED_LINE.exec(line);
    if (numbered?.groups?.label) left.push(numbered.groups.label);
    else if (lettered?.groups?.label) right.push(lettered.groups.label);
  }

  if (left.length < 2 || right.length < 2) return { left: [], right: [] };
  return { left, right };
}

/** The letter a right-hand row is printed with — "A)", "B)", … — which is what a master solution pairs on. */
export const letterFor = (index: number): string => String.fromCharCode(65 + index);

// ─── turning what was entered into what gets submitted ──────────────────────────────────────────────

/**
 * Renders a widget's state into the text the grading agent reads.
 *
 * The formats are not free choices: a matching answer is written "1 - C" because that is the pairing the
 * template's own comment says the master solution and the grading agent use, and a label answer is written
 * "1 = ..." for the same reason (see the LABEL_IMAGE note in assembleTaskNodes).
 */
/**
 * ONE TASK'S ANSWER, on its way to the server — what grading reads AND what the submission document
 * prints.
 *
 * `text` is the rendered form the whole grading pipeline already understands ("1 - B", "[x] A) ..."), and
 * it stays the answer of record. The structured fields beside it say the same thing in the shape the
 * TEMPLATE needs to print it back onto the exam: a tick belongs in its own checkbox, a pairing in the
 * connector lane, a label in its own callout box, and none of those can be recovered from a joined string
 * without parsing back out what this function just wrote.
 */
export interface StudentAnswerPayload {
  text?: string;
  imageBase64?: string;
  /** `choice` — the ticked options, as zero-based indexes into the printed option list. */
  choice?: number[];
  /** `mapping` — left row index (as a string) to every zero-based right row it was joined to. */
  match?: Record<string, number[]>;
  /** `labels` — the callout number as PRINTED (one-based, as a string) to what was written in its box. */
  labels?: Record<string, string>;
  /** `gapFill`/`promptList` — the blank's own 1-based printed number to what was written in it. */
  blanks?: Record<string, string>;
}

export function answerToPayload(
  kind: AnswerKind,
  state: TaskAnswerState | undefined,
  parsed: { options?: string[]; left?: string[] },
): StudentAnswerPayload {
  if (!state) return {};

  switch (kind) {
    case 'choice': {
      const options = parsed.options ?? [];
      const picked = (state.choices ?? []).filter(index => index < options.length).sort((a, b) => a - b);
      if (picked.length === 0) return {};
      // Every option is listed with its box ticked or not, rather than only the ticked ones: on a printed
      // sheet the grading agent sees the same — which is what "the student left B blank" looks like.
      const rendered = options.map((option, index) => `${picked.includes(index) ? '[x]' : '[ ]'} ${option}`);
      return { text: rendered.join('\n'), choice: picked };
    }
    case 'mapping': {
      const left = parsed.left ?? [];
      const chosen = left
        .map((_, index) => ({ index, letters: state.mapping?.[index] ?? [] }))
        .filter(pair => pair.letters.length > 0);
      if (chosen.length === 0) return {};
      // One line per EDGE, not per row — a row joined to two letters is two lines, exactly as many as the
      // student drew.
      const lines = chosen.flatMap(pair => pair.letters.map(letter => `${pair.index + 1} - ${letter}`));
      return {
        text: lines.join('\n'),
        // The same pairings as indexes, which is what the submission document draws from — the letters are
        // for the reader and the grading prompt, the indexes for the template's own right-hand column.
        match: Object.fromEntries(
          chosen.map(pair => [String(pair.index), pair.letters.map(letter => letter.charCodeAt(0) - 65)]),
        ),
      };
    }
    case 'labels': {
      const filled = (state.labels ?? [])
        .map((label, index) => ({ label: (label ?? '').trim(), index }))
        .filter(entry => entry.label.length > 0);
      if (filled.length === 0) return {};
      return {
        text: filled.map(entry => `${entry.index + 1} = ${entry.label}`).join('\n'),
        // Keyed by the number PRINTED in the callout box, which is the index plus one — the same key the
        // figure uses for its own boxes, so the submission prints each name back where it was typed.
        labels: Object.fromEntries(filled.map(entry => [String(entry.index + 1), entry.label])),
      };
    }
    case 'gapFill': {
      const blanks = state.blanks ?? [];
      if (blanks.length === 0 || blanks.every(entry => !entry?.trim())) return {};
      // GAP_TEXT's own OCR/extraction contract (task-types/gap-text.ts) is a numbered Markdown list, one
      // line per gap in order, "LEER" for a blank one — matching that here means grading reads an online
      // answer exactly like it would an OCR'd one, with no separate code path for either origin. EVERY
      // gap is listed, ticked or not, for the same reason `choice`/`labels` list every row above: a
      // positional list only works if a skipped position still shows up as skipped, not as absent.
      const rendered = blanks.map((entry, index) => `${index + 1}. ${(entry ?? '').trim() || 'LEER'}`);
      // Filled ones only, for print-back — a skipped gap should print as the blank line it already is
      // on paper, not with a literal "LEER" written into it (`text` above is the one that needs a
      // positional placeholder; what actually gets stamped onto the submission is a different question).
      const filled = blanks
        .map((entry, index) => ({ index, text: (entry ?? '').trim() }))
        .filter(entry => entry.text.length > 0);
      return {
        text: rendered.join('\n'),
        ...(filled.length > 0 ? { blanks: Object.fromEntries(filled.map(entry => [String(entry.index + 1), entry.text])) } : {}),
      };
    }
    case 'promptList': {
      const blanks = state.blanks ?? [];
      if (blanks.length === 0 || blanks.every(entry => !entry?.trim())) return {};
      // No established blank-placeholder convention for TRANSLATION the way GAP_TEXT's "LEER" is (see
      // gap-text.ts) — an unanswered row is simply left off its own line rather than inventing one.
      const filled = blanks
        .map((entry, index) => ({ index, text: (entry ?? '').trim() }))
        .filter(entry => entry.text.length > 0);
      if (filled.length === 0) return {};
      return {
        text: filled.map(entry => `${entry.index + 1}. ${entry.text}`).join('\n'),
        blanks: Object.fromEntries(filled.map(entry => [String(entry.index + 1), entry.text])),
      };
    }
    case 'drawing':
      return state.drawing ? { imageBase64: state.drawing } : {};
    case 'text': {
      const text = (state.text ?? '').trim();
      return text ? { text } : {};
    }
    default:
      return {};
  }
}

// ─── the widgets ────────────────────────────────────────────────────────────────────────────────────

interface WidgetProps {
  value: TaskAnswerState | undefined;
  onChange: (next: TaskAnswerState) => void;
}

/**
 * A RULED TEXTAREA — its own rule lines, drawn by the same renderer as the text sitting on them, so they
 * can never drift apart the way a transparent textarea overlaid on TYPST's printed rules did: the
 * printed rule spacing comes from typst's own line-height, a browser textarea's from its font/line-height,
 * and the two were never going to track each other line for line once a student typed enough to wrap.
 * Opaque by design — it covers the print's own lines rather than sitting on them, trading a small,
 * accepted deviation from the exact printed spacing for lines that always line up with what's typed.
 *
 * FULLY OPAQUE, background AND rule color both — no alpha anywhere. A translucent rule (or a background
 * that let the printed lines underneath show through at all) would composite with typst's OWN ruled lines
 * a few pixels away at a different spacing, which is exactly the "double lines" this exists to prevent:
 * the printed lines must be completely hidden, not blended with.
 *
 * THE TEXTAREA NEVER SCROLLS — it grows to fit every line instead, and an ordinary `overflow-y-auto` DIV
 * around it scrolls when that exceeds the box. Two earlier attempts tried to keep the rule background
 * moving WITH a scrolling textarea directly (`background-attachment: local` on the textarea itself, then
 * hand-syncing a second layer off its `scrollTop`) and both failed against the real app: a native
 * `<textarea>`'s internal scrolling is a UA-implemented replaced-element behavior that neither approach
 * reliably hooks into across browsers. Moving scrolling to a plain `<div>` sidesteps that entirely —
 * `background-attachment: local` is well-supported there, since it is now an ordinary CSS scroll container
 * instead of a textarea's own special-cased one.
 * `LINE_HEIGHT_PX` has to be a fixed pixel value (not e.g. `1.6em`) because it is baked into the gradient's
 * own stop positions in the same unit — mixing units there is what would let them drift.
 *
 * GROWING IS DONE WITH CSS (`field-sizing: content`), NOT A MEASURING LIBRARY. This used to be
 * `react-textarea-autosize`, which measures a hidden clone's `scrollHeight` in a `useLayoutEffect` — a
 * synchronous, layout-forcing DOM read on every single keystroke. That read doesn't cost what it would on
 * an empty page: this field sits over the exam's own SVG, which a Chrome trace showed the browser marking
 * almost entirely "dirty" on every such read regardless of any `contain`/`content-visibility` hint tried
 * on it — CSS containment turned out not to scope a *forced* synchronous layout in this engine at all.
 * Removing the JS measurement removes the read itself: median cost per keystroke dropped from ~59ms to
 * ~13ms against a real compiled exam page in a harness built to reproduce this exactly (a plain,
 * non-growing textarea over the same page measured the same ~13ms, confirming the *reading* was the cost,
 * not the growing). `field-sizing: content` gets the same auto-grow with no JavaScript measurement at all
 * — the browser's own layout engine sizes the box, off the main thread's critical path for this.
 * Unsupported browsers just ignore the property and get a fixed `rows={1}` textarea with its own native
 * scrollbar instead of the wrapping div's — usable, not broken.
 */
const RULED_LINE_HEIGHT_PX = 24;

function RuledTextarea({
  value,
  onChange,
  className = '',
}: WidgetProps & { className?: string }) {
  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{
        // A fixed, always-light background — deliberately NOT a Tailwind `dark:` class, which an inline
        // `backgroundColor` would just override anyway. The exam itself is a light document either way, so
        // this reads as "paper" regardless of the app's own theme.
        backgroundColor: '#ffffff',
        backgroundAttachment: 'local',
        backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${RULED_LINE_HEIGHT_PX - 1}px, rgb(180, 190, 202) ${RULED_LINE_HEIGHT_PX - 1}px, rgb(180, 190, 202) ${RULED_LINE_HEIGHT_PX}px)`,
      }}
    >
      <textarea
        value={value?.text ?? ''}
        onChange={event => onChange({ ...value, text: event.target.value })}
        rows={1}
        className={`block w-full resize-none border-0 bg-transparent px-1.5 text-sm outline-none ${className}`}
        style={
          {
            lineHeight: `${RULED_LINE_HEIGHT_PX}px`,
            paddingTop: 0,
            paddingBottom: 0,
            color: '#0f172a',
            // Not yet in the shipped CSSProperties types — hence the cast — but real and supported; see the
            // file header. `minHeight` is `rows={1}`'s own floor, kept explicit for when `field-sizing`
            // takes over box-sizing.
            fieldSizing: 'content',
            minHeight: `${RULED_LINE_HEIGHT_PX}px`,
          } as CSSProperties
        }
      />
    </div>
  );
}

/**
 * MC's checklist, overlaid directly on the printed checkbox column: one row per option.
 *
 * DRIVEN BY `rowRects`, NOT BY THE PARSED OPTIONS — `AnswerRegion.optionRows` (see exam-structure.ts) is one
 * mark per option, fired right at its own checkbox, so a wrapped option's genuinely taller row is exactly as
 * tall here as it is on the page and there are exactly as many boxes as were printed. The option TEXT comes
 * from parsing the description and is used only to label the control, which is why a parse that finds a
 * different number of lines cannot put a checkbox in the wrong place.
 */
function OverlayChoice({
  value,
  onChange,
  options,
  rowRects,
  markWidthPercent,
}: WidgetProps & {
  options: string[];
  rowRects: { topPercent: number; heightPercent: number }[];
  /** The mark's own WIDTH as a percentage of this widget's box, not a fixed CSS length — see `page.tsx`'s
   * `markWidthPercent`, which is what keeps this in step with the printed `#mc` glyph it sits over as the
   * exam itself is rendered larger or smaller. Height comes from `aspect-ratio`, not a second percentage —
   * see the comment where it's used below. */
  markWidthPercent: number;
}) {
  const selected = value?.choices ?? [];
  const toggle = (index: number) => {
    const next = selected.includes(index) ? selected.filter(item => item !== index) : [...selected, index];
    onChange({ ...value, choices: next });
  };

  return (
    <div className="relative h-full w-full">
      {rowRects.map((rect, index) => (
        <button
          key={index}
          type="button"
          onClick={() => toggle(index)}
          title={options[index]}
          aria-label={options[index]}
          aria-pressed={selected.includes(index)}
          className="absolute inset-x-0 flex items-start justify-start gap-1 pl-0 text-left"
          style={{ top: `${rect.topPercent}%`, height: `${rect.heightPercent}%` }}
        >
          {/* An X, not a filled square: the printed box is an empty outline (`#mc` on paper), and a solid
           * fill reads as a different mark than the tick a student would actually put in that outline. */}
          <span
            className={`shrink-0 flex items-center justify-center rounded-[1.5px] border ${
              selected.includes(index)
                ? 'border-brand-600 bg-white/70'
                : 'border-slate-500 bg-white/50 hover:bg-brand-50'
            }`}
            // `aspectRatio`, not a second percentage, gives the height: this row (`rowRects[index]`) can be
            // taller than one line if its option text wrapped, and a percentage of THAT height would grow
            // the mark right along with it — see `markWidthPercent`'s own comment in page.tsx.
            style={{ width: `${markWidthPercent}%`, aspectRatio: '1' }}
          >
            {selected.includes(index) && <X size="70%" strokeWidth={3} className="text-brand-600" />}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * One printed row's two connector points, as percentages of the matching widget's own box, plus whether
 * that row has an item on each side at all — the two columns can be different lengths.
 */
export interface MatchConnector {
  topPercent: number;
  leftPercent: number;
  rightPercent: number;
  hasLeft: boolean;
  hasRight: boolean;
}

/** How close a release has to land to a connector point to count as dropping on it, in CSS pixels. */
const CONNECT_SNAP_PX = 24;
/** How much wider than the visible line the invisible hit-target stroke is, in CSS pixels — a 1.5px line is
 * hard to land a click on precisely, especially with a fingertip. */
const EDGE_HIT_WIDTH_PX = 14;

/**
 * MATCHING, DRAWN THE WAY IT IS ANSWERED ON PAPER: grab the connector beside a left-hand item and drag a
 * line across to the right-hand one it belongs to. The lane between the two printed columns is exactly the
 * lane `#match-columns` leaves empty for a student to draw in, reported by typst itself (see
 * `AnswerRegion.matchRows`) — the columns are `1fr` each, so how wide they actually rendered is knowable
 * only inside typst's own layout and was previously guessed at a fixed 42%.
 *
 * MANY-TO-MANY, the way a paper answer legitimately can be: a row can end up with two lines out of it if a
 * student answers "which apply" rather than "which one" (some `#match-columns` tasks are exactly that).
 * A drag from a dot always ADDS a line — it never touches a connection already there, on either end — so
 * dragging is purely constructive; DELETING one is a separate, explicit action: clicking the line itself.
 * A dot's own on-click (no real drag) does nothing, on purpose, so an accidental tap while aiming a drag
 * can never silently erase work — "points only spawn new lines".
 *
 * THE ROWS COME FROM `connectors`, not from parsing the description: there is one handle per printed row,
 * on the side that actually has an item there. `left`/`right` are read only to label the handles.
 */
function OverlayMapping({
  value,
  onChange,
  left,
  right,
  connectors,
}: WidgetProps & { left: string[]; right: string[]; connectors: MatchConnector[] }) {
  const mapping = value?.mapping ?? {};
  const containerRef = useRef<HTMLDivElement>(null);
  /** The in-flight drag: where it started, and where the pointer is now (percent of the box, like everything else). */
  const [drag, setDrag] = useState<{ side: 'left' | 'right'; index: number; x: number; y: number } | null>(null);
  /** The edge under the pointer right now, if any — purely visual (see the edge rendering below). */
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);

  const toLocal = (event: { clientX: number; clientY: number }) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0, rect: null };
    return { x: ((event.clientX - rect.left) / rect.width) * 100, y: ((event.clientY - rect.top) / rect.height) * 100, rect };
  };

  const addEdge = (leftIndex: number, letter: string) => {
    const current = mapping[leftIndex] ?? [];
    if (current.includes(letter)) return; // already connected — dragging onto it again is a no-op, not a duplicate
    onChange({ ...value, mapping: { ...mapping, [leftIndex]: [...current, letter] } });
  };

  const removeEdge = (leftIndex: number, letter: string) => {
    const remaining = (mapping[leftIndex] ?? []).filter(entry => entry !== letter);
    const next = { ...mapping };
    if (remaining.length > 0) next[leftIndex] = remaining;
    else delete next[leftIndex];
    onChange({ ...value, mapping: next });
  };

  const finish = (event: ReactPointerEvent) => {
    const active = drag;
    setDrag(null);
    if (!active) return;
    const { x, y, rect } = toLocal(event);
    if (!rect) return;

    // Percentages are per-axis, so distance has to be compared in real pixels — the box is far wider than
    // it is tall, and "5% away" means something very different horizontally than vertically.
    const pxDistance = (aX: number, aY: number, bX: number, bY: number) =>
      Math.hypot(((aX - bX) / 100) * rect.width, ((aY - bY) / 100) * rect.height);

    // Only rows that actually have an item on the opposite side can be dropped onto.
    let best: { index: number; distance: number } | null = null;
    for (const [index, connector] of connectors.entries()) {
      const reachable = active.side === 'left' ? connector.hasRight : connector.hasLeft;
      if (!reachable) continue;
      const targetX = active.side === 'left' ? connector.rightPercent : connector.leftPercent;
      const distance = pxDistance(x, y, targetX, connector.topPercent);
      if (distance <= CONNECT_SNAP_PX && (!best || distance < best.distance)) best = { index, distance };
    }

    // No snap target: the drag is simply abandoned. Nothing is removed on a miss — see the file header.
    if (!best) return;
    if (active.side === 'left') addEdge(active.index, letterFor(best.index));
    else addEdge(best.index, letterFor(active.index));
  };

  const startDrag = (side: 'left' | 'right', index: number) => (event: ReactPointerEvent) => {
    event.preventDefault();
    // Captured on the dot itself, so the move/up handlers below keep firing once the pointer leaves it —
    // which it immediately does, since the whole gesture is "drag away from here".
    event.currentTarget.setPointerCapture(event.pointerId);
    const { x, y } = toLocal(event);
    setDrag({ side, index, x, y });
  };

  const dotHandlers = (side: 'left' | 'right', index: number) => ({
    onPointerDown: startDrag(side, index),
    onPointerMove: (event: ReactPointerEvent) => {
      if (!drag) return;
      const { x, y } = toLocal(event);
      setDrag(current => (current ? { ...current, x, y } : current));
    },
    onPointerUp: finish,
    onPointerCancel: () => setDrag(null),
  });

  const edges = Object.entries(mapping)
    .flatMap(([leftKey, letters]) => {
      const leftIndex = Number(leftKey);
      return (letters ?? []).map(letter => {
        const rightIndex = letter.charCodeAt(0) - 65;
        const from = connectors[leftIndex];
        const to = connectors[rightIndex];
        if (!from || !to || !from.hasLeft || !to.hasRight) return null;
        return {
          key: `${leftIndex}-${letter}`,
          leftIndex,
          letter,
          x1: from.leftPercent,
          y1: from.topPercent,
          x2: to.rightPercent,
          y2: to.topPercent,
        };
      });
    })
    .filter((edge): edge is NonNullable<typeof edge> => edge !== null);

  return (
    <div ref={containerRef} className="relative h-full w-full touch-none">
      {/* One 0–100 unit per percent on BOTH axes, so a line's endpoints are the same numbers the dots are
       * positioned with. `preserveAspectRatio="none"` is what makes that work on a box that is much wider
       * than it is tall, and `vector-effect` is what stops the stroke being stretched along with it.
       * `pointer-events-none` on the SVG itself keeps the lane's empty space click-through; each edge below
       * re-enables its own, so only the lines themselves are ever clickable. */}
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {edges.map(edge => (
          <g
            key={edge.key}
            className="pointer-events-auto cursor-pointer"
            onPointerEnter={() => setHoveredEdge(edge.key)}
            onPointerLeave={() => setHoveredEdge(current => (current === edge.key ? null : current))}
            onClick={() => removeEdge(edge.leftIndex, edge.letter)}
          >
            <title>{`${left[edge.leftIndex] ?? ''} – ${right[edge.letter.charCodeAt(0) - 65] ?? ''}`.trim()}</title>
            {/* Invisible, fatter twin of the visible line below — what actually catches the click. A plain
             * `<line>` has no fill, so its own hit-area is already just the stroke; this only widens that
             * stroke, and `vector-effect="non-scaling-stroke"` keeps the width in real CSS px regardless of
             * how stretched this 0–100 viewBox is over the box's actual (very non-square) aspect ratio. */}
            <line
              x1={edge.x1}
              y1={edge.y1}
              x2={edge.x2}
              y2={edge.y2}
              stroke="transparent"
              strokeWidth={EDGE_HIT_WIDTH_PX}
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={edge.x1}
              y1={edge.y1}
              x2={edge.x2}
              y2={edge.y2}
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              className={hoveredEdge === edge.key ? 'text-red-500' : 'text-brand-500'}
            />
          </g>
        ))}
        {drag && (
          <line
            x1={drag.side === 'left' ? connectors[drag.index]!.leftPercent : connectors[drag.index]!.rightPercent}
            y1={connectors[drag.index]!.topPercent}
            x2={drag.x}
            y2={drag.y}
            stroke="currentColor"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            className="pointer-events-none text-brand-400"
          />
        )}
      </svg>

      {connectors.map((connector, index) =>
        connector.hasLeft ? (
          <ConnectorDot
            key={`left-${index}`}
            title={`${index + 1}. ${left[index] ?? ''}`.trim()}
            connected={(mapping[index]?.length ?? 0) > 0}
            active={drag?.side === 'left' && drag.index === index}
            left={connector.leftPercent}
            top={connector.topPercent}
            {...dotHandlers('left', index)}
          />
        ) : null,
      )}
      {connectors.map((connector, index) =>
        connector.hasRight ? (
          <ConnectorDot
            key={`right-${index}`}
            title={`${letterFor(index)}) ${right[index] ?? ''}`.trim()}
            connected={Object.values(mapping).some(letters => letters?.includes(letterFor(index)))}
            active={drag?.side === 'right' && drag.index === index}
            left={connector.rightPercent}
            top={connector.topPercent}
            {...dotHandlers('right', index)}
          />
        ) : null,
      )}
    </div>
  );
}

/**
 * TRANSLATION's `#prompt-list`, overlaid directly on each row's own printed blank — one field per item.
 *
 * DRIVEN BY `rowRects`, NOT BY THE PARSED ITEMS — `AnswerRegion.promptRows` (see exam-structure.ts) is one
 * mark per row, fired at that row's own blank-cell top-left, so the field sits exactly where the printed
 * underline is regardless of how long that row's own source text happens to be, the same reasoning
 * `OverlayChoice` above uses for its own `rowRects`.
 */
function OverlayPromptRows({
  value,
  onChange,
  rowRects,
}: WidgetProps & {
  rowRects: { topPercent: number; leftPercent: number; widthPercent: number; heightPercent: number }[];
}) {
  const blanks = value?.blanks ?? [];
  return (
    <div className="relative h-full w-full">
      {rowRects.map((rect, index) => (
        <div
          key={index}
          className="absolute overflow-hidden rounded-[1.5px] transition-shadow focus-within:ring-2 focus-within:ring-brand-500/60"
          style={{
            top: `${rect.topPercent}%`,
            left: `${rect.leftPercent}%`,
            width: `${rect.widthPercent}%`,
            height: `${rect.heightPercent}%`,
          }}
        >
          <BlankFieldInput
            value={blanks[index] ?? ''}
            onChange={next => onChange(withBlank(value, index, next, rowRects.length))}
          />
        </div>
      ))}
    </div>
  );
}

/** The grab handle at one end of a connector lane. Small, because it sits in the margin of a real exam page. */
function ConnectorDot({
  left,
  top,
  connected,
  active,
  title,
  ...handlers
}: {
  left: number;
  top: number;
  connected: boolean;
  active: boolean;
  title: string;
} & ComponentProps<'button'>) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={`absolute h-3 w-3 cursor-grab rounded-full border transition-colors active:cursor-grabbing ${
        active
          ? 'border-brand-600 bg-brand-500'
          : connected
            ? 'border-brand-600 bg-brand-500/80'
            : 'border-slate-400 bg-white hover:border-brand-500 hover:bg-brand-50'
      }`}
      style={{ left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)' }}
      {...handlers}
    />
  );
}

/**
 * Sets one entry of the `labels` array, padding it out so index N is writable before 0..N-1 are.
 *
 * Shared by the two places a label is entered — the printed numbered lines and a labelling figure's own
 * callout boxes (`CalloutLabelInput`) — because they write the SAME array: entry i is the name for
 * number i+1 either way, which is what `answerToPayload` turns into "1 = ...".
 */
export function withLabel(
  state: TaskAnswerState | undefined,
  index: number,
  text: string,
  count: number,
): TaskAnswerState {
  const labels = [...(state?.labels ?? [])];
  while (labels.length < count) labels.push('');
  labels[index] = text;
  return { ...state, labels };
}

/** Same as `withLabel`, for the `blanks` array `gapFill`/`promptList` write instead — see its own comment. */
export function withBlank(
  state: TaskAnswerState | undefined,
  index: number,
  text: string,
  count: number,
): TaskAnswerState {
  const blanks = [...(state?.blanks ?? [])];
  while (blanks.length < count) blanks.push('');
  blanks[index] = text;
  return { ...state, blanks };
}

/**
 * The field inside ONE of a labelling figure's numbered boxes — the box `#figure-block` draws beside the
 * picture with a leader line running to the structure it names (see `AnswerRegion.calloutBoxes`). On paper
 * this box is where the student writes; online it is the same box with a caret in it.
 *
 * Transparent and borderless: the box's own printed border and its number are right there underneath, and
 * drawing a second frame inside them would read as a form stuck onto the figure. The left padding clears
 * the printed number, and the text is small because a 30mm box is small — a structure name that outruns it
 * scrolls rather than overflowing onto the drawing.
 */
export function CalloutLabelInput({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <input
      value={value}
      onChange={event => onChange(event.target.value)}
      className="h-full w-full min-w-0 border-0 bg-transparent pb-0.5 pl-[1.6em] pr-1 text-[11px] leading-tight text-slate-900 outline-none focus:bg-brand-50/60"
    />
  );
}

/**
 * The field over ONE printed blank — a `#gap` scattered mid-sentence (rendered directly by
 * `AnswerSlotOverlay` in student-exam/page.tsx, the same way it renders `calloutBoxes`: there is no single
 * widget box to route through `OverlayAnswer`, only scattered points — see `AnswerRegion.gapBoxes`), or a
 * `#prompt-list` row's own underline (rendered by `OverlayPromptRows` below, since a prompt-list DOES have
 * one). Unlike `CalloutLabelInput`, there is no printed number to clear inside the box itself (a gap's
 * number lives in the surrounding sentence, not on the blank; a prompt-list row's number is its own
 * separate grid cell, outside this box entirely), so no left padding.
 */
export function BlankFieldInput({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <input
      value={value}
      onChange={event => onChange(event.target.value)}
      className="h-full w-full min-w-0 border-0 bg-transparent px-1 pb-0.5 text-[11px] leading-tight text-slate-900 outline-none focus:bg-brand-50/60"
    />
  );
}

/**
 * `DrawingCanvas` manages its own strokes internally (see that component's header) and never reads `value`
 * back — it only ever reports outward via `onChange`. So when its own emitted data URL round-trips through
 * the page's answer state and this widget re-renders, `DrawingCanvas` re-rendering is pure wasted work: it
 * has nothing new to draw. `React.memo` lets it skip that, but only if `onChange` is referentially stable —
 * a fresh closure per render (as a plain inline arrow function here would be, since it captures `value`,
 * which is exactly what changes on every stroke) defeats the memo just as surely as not having one. Reading
 * the latest `value` from a ref instead keeps the callback itself stable across renders.
 */
const DrawingOverlay = memo(function DrawingOverlay({
  value,
  onChange,
  pageImageUrl,
  crop,
  promptCrops,
}: Pick<WidgetProps, 'value' | 'onChange'> & {
  pageImageUrl?: string;
  crop?: PageCrop;
  promptCrops?: { crop: PageCrop; aspect: number; pageImageUrl: string }[];
}) {
  const valueRef = useRef(value);
  valueRef.current = value;
  const handleChange = useCallback(
    (dataUrl: string | null, strokes: SavedStroke[]) =>
      onChange({ ...valueRef.current, drawing: dataUrl, strokes: strokes.length > 0 ? strokes : undefined }),
    [onChange],
  );
  return (
    <DrawingCanvas
      initialStrokes={value?.strokes}
      initialDrawing={value?.drawing ?? null}
      pageImageUrl={pageImageUrl}
      crop={crop}
      promptCrops={promptCrops}
      onChange={handleChange}
    />
  );
});

/**
 * The in-place input that sits ON the printed answer region, filling exactly its box — every answerable
 * task now renders this way, `inline` kinds included, since `answer-region`'s id convention (see
 * `describeAnswerSpace`/exam.typ) gives even a checklist or a plotted grid a real box to sit on.
 *
 * Deliberately near-transparent and borderless until touched: the page underneath is the real exam, and a
 * field with a heavy frame would read as a form pasted over it rather than as the space the sheet already
 * provides.
 */
export function OverlayAnswer({
  kind,
  value,
  onChange,
  lines,
  options,
  left,
  right,
  rowRects,
  markWidthPercent,
  connectors,
  promptRects,
  pageImageUrl,
  crop,
  promptCrops,
}: WidgetProps & {
  kind: AnswerKind;
  lines?: number;
  options?: string[];
  left?: string[];
  right?: string[];
  /** `choice` only — see `OverlayChoice`'s own doc comment. */
  rowRects?: { topPercent: number; heightPercent: number }[];
  /** `choice` only — see `OverlayChoice`'s `markWidthPercent`. */
  markWidthPercent?: number;
  /** `mapping` only — see `OverlayMapping`'s own doc comment. */
  connectors?: MatchConnector[];
  /** `promptList` only — see `OverlayPromptRows`'s own doc comment. Its own shape, not `rowRects`: unlike
   * `choice`'s checkbox column (always flush left, full width), a prompt-list row's blank cell has its own
   * `x`/width within the box. */
  promptRects?: { topPercent: number; leftPercent: number; widthPercent: number; heightPercent: number }[];
  /** `drawing` only — this region's own printed page (a PNG) and its rectangle in it, for the enlarged
   * drawing view. */
  pageImageUrl?: string;
  crop?: PageCrop;
  /** `drawing` only — a picture of the task's own printed PROMPT (not its answer region), and every
   * ancestor's own prompt above it for a subtask, shown stacked above the zoomed editor so a student
   * doesn't have to close it to re-read the question. A picture, not the task's `description` text, so it
   * shows exactly what was printed (formulas, emphasis, wrapped figures) instead of a plain re-typed string
   * — see `AnswerSlot.promptBoxes` in page.tsx, where these are carved out of each ancestor's own whole
   * box. Each entry pairs the crop fractions with the box's real aspect ratio (the fractions alone aren't
   * comparable to each other — see `DrawingCanvas`'s own doc comment). Each entry also carries its OWN page
   * image: an ancestor can sit on an earlier page than the leaf's own answer (a sibling subtask's growing
   * answer can push a later one onto the next page), so this can't share one image across every entry.
   * Absent when there's nothing to carve (no separate answer box to measure from).
   */
  promptCrops?: { crop: PageCrop; aspect: number; pageImageUrl: string }[];
}) {
  // `fill` makes the canvas take the whole printed region and float its toolbar in a corner on hover.
  if (kind === 'drawing') {
    return (
      <DrawingOverlay
        value={value}
        onChange={onChange}
        pageImageUrl={pageImageUrl}
        crop={crop}
        promptCrops={promptCrops}
      />
    );
  }
  if (kind === 'choice') {
    return (
      <OverlayChoice
        value={value}
        onChange={onChange}
        options={options ?? []}
        rowRects={rowRects ?? []}
        markWidthPercent={markWidthPercent ?? 0}
      />
    );
  }
  if (kind === 'mapping') {
    return (
      <OverlayMapping
        value={value}
        onChange={onChange}
        left={left ?? []}
        right={right ?? []}
        connectors={connectors ?? []}
      />
    );
  }

  if (kind === 'promptList') {
    return <OverlayPromptRows value={value} onChange={onChange} rowRects={promptRects ?? []} />;
  }

  if (kind === 'labels') {
    const rowCount = Math.max(1, lines ?? 3);
    const labels = value?.labels ?? [];
    return (
      // One row per printed line, so each field lands on the line carrying its number. Only reached when
      // the task has no labelling figure of its own — where it does, the fields sit in that figure's own
      // callout boxes instead (see `CalloutLabelInput`, positioned by the page).
      <div className="grid h-full w-full" style={{ gridTemplateRows: `repeat(${rowCount}, 1fr)` }}>
        {Array.from({ length: rowCount }, (_, index) => (
          <input
            key={index}
            value={labels[index] ?? ''}
            onChange={event => onChange(withLabel(value, index, event.target.value, rowCount))}
            className="min-w-0 self-end border-0 bg-transparent px-6 pb-0.5 text-sm text-slate-900 outline-none focus:bg-brand-50/60"
          />
        ))}
      </div>
    );
  }

  if (kind === 'text') {
    return <RuledTextarea value={value} onChange={onChange} className="focus:bg-brand-50/50" />;
  }

  return null;
}
